import { createSlice } from "@reduxjs/toolkit";
import { ConnectionState } from "livekit-client";
import _ from "lodash";
import { Quaternion, Vector3 } from "three";

import { VehicleSettings } from "@skydio/channels/src/update_vehicle_settings_pb";
import { degToRad, secondsToMilliseconds } from "@skydio/math";
import { FlightPhaseEnum } from "@skydio/pbtypes/pbtypes/gen/common/flight_phase_enum_pb";
import { Auth } from "@skydio/pbtypes/pbtypes/vehicle/infrastructure/flight_deck/auth_pb";
import { NavigationMode } from "@skydio/pbtypes/pbtypes/vehicle/infrastructure/flight_deck/navigation_mode_pb";
import { InterceptOrbitPoiMode_Enum } from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/orbit_poi/orbit_poi";
import { toast } from "@skydio/rivet-ui";
import { convertTransPbToObject3 } from "@skydio/shared_ui/utils/math";
import { WebRTCConnectionState } from "@skydio/skybus/src/transport/types";
import { CellularModemStates } from "@skydio/channels/src/cellular_modem_states_pb";
import { SimState } from "@skydio/pbtypes/pbtypes/gen/cellular_sim_configs/sim_state_pb";
import { trackAnalytic } from "utils/trackEvent";

import { DEFAULT_FLY_TO_POINT_HEIGHT } from "components/main/flight_player/map/MapViewerWithCurrentVehicle/POI/constants";
import { teleopActions } from "state/teleop/slice";
import {
  areNumbersApproximatelyEqual,
  areVectorsApproximatelyEqual,
  kMilliTol,
} from "utils/common";

import {
  getActionForKey,
  getCamNavHeadingPitch,
  getDirectionForKey,
  getIsMostRecentVehiclePilot,
  getVehiclePosition,
  getCorrectedNominalVehicleTranslationSpeed,
  isInFlight,
  isInPostFlight,
  isLanding,
} from "./partialSelectors";
import {
  isHeadingMovement,
  isPitchMovement,
  isPositionMovement,
  MANUAL_FLIGHT_BLOCKERS,
} from "./types";
import {
  augmentedKeyboardHeadingRate,
  augmentedKeyboardPitchRate,
  calculateNavHeadingPitchFromGimbalAndVehicleOrientation,
  calculateNavTCommand,
  calculateNewPositionOnTap,
  cubicDeadzoneFunction,
  GAMEPAD_DEADZONE,
  gamepadModeMappings,
  getSpeedSettings,
  getTeleopAntMessageClassName,
  getZeroMovementInput,
  getZoomCorrection,
  isGamepadSupported,
  MAX_GIMBAL_PITCH_RADIANS,
  MIN_GIMBAL_PITCH_RADIANS,
  obstacleSafetyToObstacleMode,
  supportedGamepadMovementInputs,
  TAP_DURATION,
  TAP_PITCH_YAW_CORRECTION_FACTOR,
  toTransPb,
} from "./utils";

import type { Action, PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import type { VehicleStatus } from "@skydio/channels/src/ambassador_vehicle_status_pb";
import type { DeviceNodeHealth } from "@skydio/channels/src/device_node_health_summary_pb";
import type { FlightPhase as FlightPhasePb } from "@skydio/channels/src/flight_phase_pb";
import type { GimbalFlashlightStatus } from "@skydio/channels/src/gimbal_flashlight_status_pb";
import type { LandingPadTrackerSimplifiedState } from "@skydio/channels/src/landing_pad_tracker_simplified_state_pb";
import type { MotionPlannerStatus } from "@skydio/channels/src/motion_planner_status_pb";
import type { ParachuteStatus } from "@skydio/channels/src/parachute_status_pb";
import type { PilotResponse } from "@skydio/channels/src/pilot_response_pb";
import type { ReturnToDockInfo } from "@skydio/channels/src/return_to_dock_info_pb";
import type { SafeLandingInfo } from "@skydio/channels/src/safe_landing_info_pb";
import type { ServerInfo } from "@skydio/channels/src/server_info_pb";
import type { SkillStatusMinimal } from "@skydio/channels/src/skill_status_pb";
import type { SpeakerMicAudioFiles } from "@skydio/channels/src/speaker_mic_list_audio_files_pb";
import type { SpeakerMicStatus } from "@skydio/channels/src/speaker_mic_status_pb";
import type { SpotlightSystemStatus } from "@skydio/channels/src/spotlight_system_status_pb";
import type { StreamCameraCalibration } from "@skydio/channels/src/stream_camera_calibration_pb";
import type { MinimalAutoRegionState } from "@skydio/channels/src/user_camera_autoregion_state_pb";
import type { CameraStatus } from "@skydio/channels/src/user_camera_status_ng_pb";
import type { ValueType } from "@skydio/core";
import type { flight_phase_t } from "@skydio/lcm/types/common/flight_phase_t";
import type { TransPb } from "@skydio/pbtypes/pbtypes/gen/body/trans_pb";
import type { CameraIntrinsics } from "@skydio/pbtypes/pbtypes/gen/calibration/camera_intrinsics_pb";
import type { BlockingPrompt } from "@skydio/pbtypes/pbtypes/gen/user_prompts/blocking_prompt_pb";
import type { PortStatusToConsumers } from "@skydio/pbtypes/pbtypes/infrastructure/drivers/attachment_manager/attachment_manager_pb";
import type { DeviceLinksSummary } from "@skydio/pbtypes/pbtypes/infrastructure/flight_connect/link_summary_pb";
import type { Flight } from "@skydio/pbtypes/pbtypes/vehicle/infrastructure/flight_deck/flight_pb";
import type { GoToWaypointStatus } from "@skydio/pbtypes/pbtypes/vehicle/skills/waypoints_pb";
import type { ScenarioStatus } from "@skydio/pbtypes/pbtypes/sim/scenario_sim/scenario_status_pb";
import type { MinimalSubjectStatus } from "@skydio/channels/src/subject_status_pb";
import type { Status as DropperStatus } from "@skydio/channels/src/dropper_status_pb";
import type { GamepadMode } from "hooks/useUserOverrides";
import type { AppState } from "state/store";
import type {
  AutonomyWidgetStatus,
  DroneSenseMission,
  GamepadMovementInputs,
  MouseOrientation,
  MovementDirection,
  PilotState,
  TeleopAction,
} from "./types";
import type { TurtleModeDirection } from "@skydio/pbtypes/pbtypes/gen/airhawk_driver/turtle_mode_direction_pb";
import { getWaypointSpeed } from "components/main/mission_editor_v2/shared_constants";

const initialState: PilotState = {
  vehicleConnection: {
    clientId: "",
    vehicleId: "",
    active: false,
    success: false,
    error: null,
    downloadBitrate: 0,
    uploadBitrate: 0,
    status: WebRTCConnectionState.CLOSED,
  },
  sessionId: "",
  runmode: "",
  activeFaults: [],
  hasReceivedActiveFaultsLcm: false,
  takeoffRequested: false,
  commandeerRequested: false,
  obstacleSetting: { safety: 1.0, mode: VehicleSettings.ObstacleMarginMode.DEFAULT_OBSTACLES },
  // TODO(sam): add UI to customize keybindings
  // https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values
  // Internet Explorer, Edge (16 and earlier), and Firefox (36 and earlier) use "Left", "Right",
  // "Up", and "Down" instead of "ArrowLeft", "ArrowRight", "ArrowUp", and "ArrowDown".
  movementKeybindings: {
    forwards: "w",
    backwards: "s",
    left: "a",
    right: "d",
    up: " ",
    down: "Shift",
    gimbalUp: "ArrowUp",
    gimbalDown: "ArrowDown",
    yawLeft: "ArrowLeft",
    yawRight: "ArrowRight",
  },
  teleopActionKeybindings: {
    toggleObstacleAvoidance: "r",
    togglePointerLock: "c",
    increaseSpeed: "]",
    decreaseSpeed: "[",
    zoomIn: "=",
    zoomOut: "-",
    increaseExposure: ".",
    decreaseExposure: ",",
    resetExposure: "/",
    takePhoto: "p",
    workflowNext: "n",
    workflowPrevious: "b",
    launchOrReturnToDock: "Enter",
    cancel: "Backspace",
    toggleControlsMenu: "`",
    autoFocusCenter: "f",
    clearFocusCenter: "v",
    zoomOneShortcut: "1",
    zoomTwoShortcut: "2",
    zoomThreeShortcut: "3",
    zoomFourShortcut: "4",
    zoomFiveShortcut: "5",
    zoomSixShortcut: "6",
    toggleStrobeLights: "i",
    thermalToggle: "t",
    boost: "q",
    crawl: "e",
    resetGimbalPitch: "z",
    selectSubject: "[select-subject]", //  Every gamepad button has a key binding, but since we don't have a keyboard hotkey for selectSubject, we use a dummy key.
    toggleRgbLights: "l",
    toggleARMarkers: "m",
    createNewMarker: "k",
    toggleCrosshairInfo: "j",
    toggleCurrentMission: "x",
    toggleARStreetOverlays: "o",
    toggleSubjectTracking: "u",
    toggleSubjectFollowing: "y",
    __DEMO_ONLY_triggerDedroneAlert: "9",
    deployParachuteFirstKey: "1",
    deployParachuteSecondKey: "8",
    deployDropper: "7",
  },
  keyboardMovementInputs: getZeroMovementInput(true),
  teleopActionInputs: {
    toggleObstacleAvoidance: -1,
    togglePointerLock: -1,
    increaseSpeed: -1,
    decreaseSpeed: -1,
    zoomIn: -1,
    zoomOut: -1,
    increaseExposure: -1,
    decreaseExposure: -1,
    resetExposure: -1,
    takePhoto: -1,
    workflowNext: -1,
    workflowPrevious: -1,
    launchOrReturnToDock: -1,
    cancel: -1,
    toggleControlsMenu: -1,
    autoFocusCenter: -1,
    clearFocusCenter: -1,
    zoomOneShortcut: -1,
    zoomTwoShortcut: -1,
    zoomThreeShortcut: -1,
    zoomFourShortcut: -1,
    zoomFiveShortcut: -1,
    zoomSixShortcut: -1,
    toggleStrobeLights: -1,
    thermalToggle: -1,
    boost: -1,
    crawl: -1,
    resetGimbalPitch: -1,
    selectSubject: -1,
    toggleRgbLights: -1,
    toggleARMarkers: -1,
    createNewMarker: -1,
    toggleCrosshairInfo: -1,
    toggleCurrentMission: -1,
    toggleARStreetOverlays: -1,
    toggleSubjectTracking: -1,
    toggleSubjectFollowing: -1,
    __DEMO_ONLY_triggerDedroneAlert: -1,
    deployParachuteFirstKey: -1,
    deployParachuteSecondKey: -1,
    deployDropper: -1,
  },
  teleopActionSources: {
    toggleObstacleAvoidance: "keyboard",
    togglePointerLock: "keyboard",
    increaseSpeed: "keyboard",
    decreaseSpeed: "keyboard",
    zoomIn: "keyboard",
    zoomOut: "keyboard",
    increaseExposure: "keyboard",
    decreaseExposure: "keyboard",
    resetExposure: "keyboard",
    takePhoto: "keyboard",
    workflowNext: "keyboard",
    workflowPrevious: "keyboard",
    launchOrReturnToDock: "keyboard",
    cancel: "keyboard",
    toggleControlsMenu: "keyboard",
    autoFocusCenter: "keyboard",
    clearFocusCenter: "keyboard",
    zoomOneShortcut: "keyboard",
    zoomTwoShortcut: "keyboard",
    zoomThreeShortcut: "keyboard",
    zoomFourShortcut: "keyboard",
    zoomFiveShortcut: "keyboard",
    zoomSixShortcut: "keyboard",
    toggleStrobeLights: "keyboard",
    thermalToggle: "keyboard",
    boost: "keyboard",
    crawl: "keyboard",
    resetGimbalPitch: "keyboard",
    selectSubject: "keyboard",
    toggleRgbLights: "keyboard",
    toggleARMarkers: "keyboard",
    createNewMarker: "keyboard",
    toggleCrosshairInfo: "keyboard",
    toggleCurrentMission: "keyboard",
    toggleARStreetOverlays: "keyboard",
    toggleSubjectTracking: "keyboard",
    toggleSubjectFollowing: "keyboard",
    __DEMO_ONLY_triggerDedroneAlert: "keyboard",
    deployParachuteFirstKey: "keyboard",
    deployParachuteSecondKey: "keyboard",
    deployDropper: "keyboard",
  },
  teleopSubjectTrackingControlMode: "mouse",
  mouseInput: {
    orientation: {},
    locked: false,
    lastMouseMove: -1,
  },
  gamepadConnected: false,
  gamepadMapping: "standard",
  gamepadMovementInputs: getZeroMovementInput(false),
  clickToFly: {
    clickStart: new Vector3(0, 0, 0),
    clickEnd: new Vector3(0, 0, 0),
    scrollEnd: new Vector3(0, 0, 0),
    active: -1, // records time click to fly is activated
    displayTrail: false, // if true displays a trail of markers between clickEnd and scroll End
  },
  activePromptsList: [],
  buttonInputs: {},
  flightId: "",
  flightPhaseId: FlightPhaseEnum.Enum.UNKNOWN,
  mostRecentPilot: {
    clientId: "",
    updatedAt: -1, // indicates we haven't received any pilot info yet
    // The current pilot's uuid as sent by the vehicle in the vehicle status (comes from PILOT_STATUS)
    pilotCloudUserId: undefined,
  },
  headingControlMode: "velocity",
  pitchControlMode: "velocity",
  positionControlMode: "velocity",
  desiredPose: {},
  desiredZoomLevel: 1,
  desiredVehicleNominalTranslationSpeed:
    getSpeedSettings()[VehicleSettings.ObstacleMarginMode.DEFAULT_OBSTACLES].default,
  vehicleNominalTranslationSpeed:
    getSpeedSettings()[VehicleSettings.ObstacleMarginMode.DEFAULT_OBSTACLES].default,
  zoomCorrectionFactor: 1.0,
  vehicleSpeedChangeTime: -1,
  vehicleSpeedSettings: getSpeedSettings()[VehicleSettings.ObstacleMarginMode.DEFAULT_OBSTACLES],
  crawlModeActive: false,
  boostModeActive: false,
  ignoreObstacleBasedSpeedSetting: false,
  displayPointerLockInnerRing: true,
  goToWaypointStatus: {},
  thirdPersonViewActive: false,
  manualFlightBlockers: { blocked: false, blockers: [], blockerRemovalTimestamp: -1 },
  isAutonomyOff: false,
  configReceived: false,
  defaultPoiHeight: DEFAULT_FLY_TO_POINT_HEIGHT,
  defaultOrbitInterceptMode: InterceptOrbitPoiMode_Enum.PERPENDICULAR,
  defaultOrbitDirection: "counterclockwise",
  defaultPathBetweenPointsMode: "upAndOver",
  defaultLookAtPointActive: false,
  lookAtFinalPointActive: false,
  rampHeightModeActive: false,
  nightSenseActive: false,
  deviceLinksSummary: {},
  deviceNodeHealth: {},
  droneSense: {
    requestStatus: "UNINITIALIZED",
  },
  lastKnownFlyingFlightId: "",
  farAwayFromLaunchPoint: false,
  farAwayFromLaunchPointDismissed: false,
  flyToPointStandoffDistance: 0,
  cellularModemStates: new CellularModemStates(),
  autonomousFlightSpeed: { policySetting: getWaypointSpeed(false).MAX, userSetting: null },
  remoteIdBroadcasting: false,
};

export interface KeyActionPayload {
  key: string;
  pressed: boolean;
  repeat: boolean; // tapped (False) or held (True)
}

export type ButtonKeys = "elevation" | "range";

export interface ButtonActionPayload {
  key: ButtonKeys;
  value?: number;
}

export interface FlightDeckAuthLevel {
  authLevel: ValueType<Auth.AccessLevelMap>;
  clientId: string;
}

/**
 * Helper function to return the next pilot client's ID for flight deck auth responses and pilot
 * response skybus messages.
 */
function nextPilotClientId(
  state: Readonly<PilotState>,
  { isPilot, clientId }: { isPilot: boolean; clientId: string }
): string {
  if (isPilot) {
    // clientId has become (or still is) the pilot
    return clientId;
  } else if (state.mostRecentPilot.clientId === clientId) {
    // current pilot has had access revoked
    return "";
  } else {
    // a non-pilot has authed with flight-deck, ignore
    return state.mostRecentPilot.clientId;
  }
}

function nextCommandeerRequested(state: Readonly<PilotState>) {
  return state.commandeerRequested ? !getIsMostRecentVehiclePilot(state) : false;
}

// helper for formatting string for why sim slot is not active
function getSimSlotInactiveReason(
  simState: SimState.EnumMap[keyof SimState.EnumMap],
  slotNumber: number
) {
  switch (simState) {
    case SimState.Enum.KNOCONFIG:
      return `Slot ${slotNumber} is not configured`;
    default:
      return `No sim detected in slot ${slotNumber}`;
  }
}

const {
  reducer,
  actions: pilotActions,
  caseReducers: pilotCaseReducers,
} = createSlice({
  name: "pilot",
  initialState,
  reducers: {
    handleLivekitConnectorState(
      state,
      {
        payload,
      }: PayloadAction<{ clientId: string; vehicleId: string; connectionState: ConnectionState }>
    ) {
      state.vehicleConnection.vehicleId = payload.vehicleId;
      state.vehicleConnection.clientId = payload.clientId;
      state.vehicleConnection.status = payload.connectionState;
      switch (payload.connectionState) {
        case ConnectionState.Reconnecting:
          state.vehicleConnection.active = true;
          state.vehicleConnection.success = false;
          break;
        case ConnectionState.Connected:
          state.vehicleConnection.active = false;
          state.vehicleConnection.success = true;
          break;
        case ConnectionState.Disconnected:
          state.vehicleConnection.active = false;
          state.vehicleConnection.success = false;
          break;
      }
    },
    handleFlightdeckAuthLevel(state, { payload }: PayloadAction<FlightDeckAuthLevel>) {
      const isPilot = (payload.authLevel ?? 0) >= Auth.AccessLevel.PILOT;
      state.mostRecentPilot.updatedAt = Date.now();
      state.mostRecentPilot.clientId = nextPilotClientId(state, {
        isPilot,
        clientId: payload.clientId,
      });
      state.commandeerRequested = nextCommandeerRequested(state);
    },
    handleVehicleStatus(state, action: PayloadAction<{ vehicleStatus: VehicleStatus }>) {
      state.flightState = action.payload.vehicleStatus.getFlightState();
      state.obstacleSetting.safety = action.payload.vehicleStatus.getObstacleSafety();
      // set obstacle mode if it has changed
      const obstacleMode = obstacleSafetyToObstacleMode(state.obstacleSetting.safety);
      if (state.obstacleSetting.mode !== obstacleMode) state.obstacleSetting.mode = obstacleMode;
      // set vehicle speed setting based on obstacle margin that the planner is using and whether
      // the user has fast flight enabled if obstacle based speed settings are enabled
      if (!state.ignoreObstacleBasedSpeedSetting) {
        const speedSettings = getSpeedSettings(
          /*nightSense*/ state.nightSenseActive,
          /*twilight*/ false
        )[obstacleMode];
        // only set speed settings if they have changed
        if (
          state.vehicleSpeedSettings.max !== speedSettings.max ||
          state.vehicleSpeedSettings.min !== speedSettings.min ||
          state.vehicleSpeedSettings.default !== speedSettings.default
        ) {
          state.vehicleSpeedSettings = speedSettings;
          state.vehicleNominalTranslationSpeed = _.clamp(
            state.vehicleNominalTranslationSpeed,
            speedSettings.min,
            speedSettings.max
          );
          state.desiredVehicleNominalTranslationSpeed = _.clamp(
            state.desiredVehicleNominalTranslationSpeed,
            speedSettings.min,
            speedSettings.max
          );
        }
      }
      if (isInFlight(state) || isInPostFlight(state)) {
        state.takeoffRequested = false;
      }
      if (isInFlight(state)) {
        // record the last known flying flight id
        state.lastKnownFlyingFlightId = state.flightId;
      }
      state.rssi = action.payload.vehicleStatus.getRssi();
      state.vehicleAmbassadorStatusLastUpdated = new Date();
      // Extract latest pilot cloud user id
      state.mostRecentPilot.pilotCloudUserId = action.payload.vehicleStatus.getPilotCloudUserId();
    },
    handleVehicleSettings(
      state,
      action: PayloadAction<{ vehicleSettings: VehicleSettings.AsObject }>
    ) {
      const twilightModeActive =
        action.payload.vehicleSettings.navigationMode === NavigationMode.Mode.TWILIGHT;
      const gpsFlightActive =
        action.payload.vehicleSettings.navigationMode === NavigationMode.Mode.AUTONOMY_OFF;
      const nightAutonomyNavigationMode =
        action.payload.vehicleSettings.navigationMode === NavigationMode.Mode.NIGHT_AUTONOMY;
      const nightSenseEnabled = !action.payload.vehicleSettings.nightsenseDisabled;
      state.nightSenseActive = nightAutonomyNavigationMode && nightSenseEnabled;
      // Modify speed setting based on navigation mode.
      let desiredSpeedSettings = undefined;
      if (
        twilightModeActive ||
        (nightAutonomyNavigationMode && !nightSenseEnabled) ||
        gpsFlightActive
      ) {
        // this ensures that any obstacles safety value based speed setting does not override this
        state.ignoreObstacleBasedSpeedSetting = true;
        desiredSpeedSettings = getSpeedSettings(
          /*nightSense*/ false,
          /*twilightMode*/ twilightModeActive
        )[VehicleSettings.ObstacleMarginMode.NO_OBSTACLES];
      } else {
        state.ignoreObstacleBasedSpeedSetting = false;
      }
      if (desiredSpeedSettings !== undefined) {
        // change speed settings only the first time we get this message, resolves SW-54332
        if (JSON.stringify(state.vehicleSpeedSettings) !== JSON.stringify(desiredSpeedSettings)) {
          state.vehicleSpeedSettings = desiredSpeedSettings;
          state.vehicleNominalTranslationSpeed = desiredSpeedSettings.default;
          state.desiredVehicleNominalTranslationSpeed = desiredSpeedSettings.default;
        }
      }
    },
    handleActiveFaultsPb(state, action: PayloadAction<number[]>) {
      if (!state.hasReceivedActiveFaultsLcm) {
        state.activeFaults = action.payload;
      }
    },
    handleActiveFaultsLcm(state, action: PayloadAction<number[]>) {
      state.hasReceivedActiveFaultsLcm = true;
      state.activeFaults = action.payload;
    },
    handleFlight(state, action: PayloadAction<Flight>) {
      state.flightId = action.payload.getFlightId();
    },
    requestTakeoff(state) {
      state.takeoffRequested = true;
    },
    cancelTakeoffRequest(state) {
      state.takeoffRequested = false;
    },
    requestCommandeer(state) {
      state.takeoffRequested = true;
      state.commandeerRequested = true;
    },
    handleKeyboardInput(state, { payload }: PayloadAction<KeyActionPayload>) {
      // register movement input if the key is a movement key, and manual flight hasn't been blocked
      // for atleast 250 ms
      const direction = getDirectionForKey(state, payload.key);
      const blockerTimeout = performance.now() - state.manualFlightBlockers.blockerRemovalTimestamp;
      const vehicleIsLanding = isLanding(state);
      if (direction) {
        const isKeyPressUnblocked = !state.manualFlightBlockers.blocked && blockerTimeout > 250;
        const isKeyInputEmpty = state.keyboardMovementInputs[direction] < 0;

        if (isKeyPressUnblocked && payload.pressed) {
          if (isKeyInputEmpty) {
            state.keyboardMovementInputs[direction] = performance.now();
          }

          const isKeyTapped =
            performance.now() - state.keyboardMovementInputs[direction] <
            secondsToMilliseconds(TAP_DURATION);
          if (isKeyTapped) {
            // If a key is tapped, enter position mode
            const isPointerLocked = state.mouseInput.locked;
            const isCrawling = state.crawlModeActive;
            if (isPitchMovement(direction) && !isPointerLocked) {
              state.pitchControlMode = "position";

              // Update the desired pose pitch by the tap amount
              const currentPitch = state.desiredPose.pitch || getCamNavHeadingPitch(state)?.pitch;
              if (currentPitch !== undefined) {
                const pitchChange =
                  augmentedKeyboardPitchRate(direction, state.zoomCorrectionFactor, isCrawling) *
                  TAP_DURATION *
                  (isCrawling ? 1 : TAP_PITCH_YAW_CORRECTION_FACTOR); // don't double correct
                state.desiredPose.pitch = _.clamp(
                  currentPitch + pitchChange,
                  MIN_GIMBAL_PITCH_RADIANS,
                  MAX_GIMBAL_PITCH_RADIANS
                );
                state.desiredPose.timestamp = performance.now();
              }
            } else if (isHeadingMovement(direction) && !isPointerLocked) {
              state.headingControlMode = "position";

              // Update the desired pose pitch by the tap amount
              const currentHeading =
                state.desiredPose.heading || getCamNavHeadingPitch(state)?.heading;
              if (currentHeading !== undefined) {
                const headingChange =
                  augmentedKeyboardHeadingRate(direction, state.zoomCorrectionFactor, isCrawling) *
                  TAP_DURATION *
                  (isCrawling ? 1 : TAP_PITCH_YAW_CORRECTION_FACTOR); // don't double correct
                state.desiredPose.heading = currentHeading + headingChange;
                state.desiredPose.timestamp = performance.now();
              }
            } else if (isPositionMovement(direction) && !vehicleIsLanding) {
              state.positionControlMode = "position";
              const navHeadingPitch = getCamNavHeadingPitch(state);
              const nominalSpeed = getCorrectedNominalVehicleTranslationSpeed(state);
              if (navHeadingPitch) {
                state.desiredPose.position = calculateNewPositionOnTap(
                  state.desiredPose.position || getVehiclePosition(state),
                  direction,
                  navHeadingPitch.heading,
                  nominalSpeed
                );
                state.desiredPose.timestamp = performance.now();
              }
            }
          }
        }
        // always allow clearing keyboard movement inputs
        if (!payload.pressed) {
          state.keyboardMovementInputs[direction] = -1;
        }
      }

      const action = getActionForKey(state, payload.key);
      if (action) {
        // record timestamp of first press (to enable detection of press and hold)
        if (payload.pressed && state.teleopActionInputs[action] < 0) {
          state.teleopActionInputs[action] = performance.now();
        }
        if (!payload.pressed) {
          state.teleopActionInputs[action] = -1;
        }
      }
    },
    setPitchControlMode(state, { payload }: PayloadAction<"velocity" | "position">) {
      state.pitchControlMode = payload;
      if (payload === "velocity") {
        // reset desired pose pitch to avoid snapping back to the last pitch
        state.desiredPose.pitch = undefined;
      }
    },
    setHeadingControlMode(state, { payload }: PayloadAction<"velocity" | "position">) {
      state.headingControlMode = payload;
      if (payload === "velocity") {
        // reset desired pose heading to avoid snapping back to the last heading
        state.desiredPose.heading = undefined;
      }
    },
    setPositionControlMode(state, { payload }: PayloadAction<"velocity" | "position">) {
      state.positionControlMode = payload;
      if (payload === "velocity") {
        // reset desired pose position to avoid snapping back to the last position
        state.desiredPose.position = undefined;
      }
    },
    setGamepadConnected(
      state,
      { payload }: PayloadAction<{ connected: boolean; mapping: GamepadMappingType }>
    ) {
      const antMessageClassName = getTeleopAntMessageClassName();
      // TODO(divyanshu): move the messages to a separate component so we can use LargeMessageConfig
      // we only support standard mapping for now
      state.gamepadMapping = payload.mapping;
      if (payload.connected) {
        if (!isGamepadSupported(payload.mapping)) {
          toast.warning("Gamepad not compatible. Please use a gamepad with standard mapping.", {
            className: antMessageClassName,
            id: "gamepad-mapping-error",
            duration: Infinity,
          });
          trackAnalytic("streaming.gamepad_incompatible", {
            mapping: payload.mapping,
          });
        }
      } else {
        // remove the message when gamepad is disconnected
        toast.dismiss("gamepad-mapping-error");
      }
      // get connected state and display connection message if mapping is okay
      if (state.gamepadConnected !== payload.connected) {
        if (isGamepadSupported(payload.mapping)) {
          toast.info(payload.connected ? "Gamepad connected" : "Gamepad disconnected", {
            className: antMessageClassName,
            id: "gamepad-connection",
          });
        }
        state.gamepadConnected = payload.connected;
      }
    },
    handleGamepadMovementInput(
      state,
      {
        payload,
      }: PayloadAction<{
        mode: GamepadMode;
        expoLevel: number;
        gamepadMovementInputs: { [key in GamepadMovementInputs]?: number };
      }>
    ) {
      const mapping = gamepadModeMappings[payload.mode];
      for (const inputInfo of supportedGamepadMovementInputs()) {
        const value = payload.gamepadMovementInputs[inputInfo.input];
        if (value !== undefined) {
          const directions = mapping[inputInfo.input];
          // apply cubic deadzone function to get stick value for use in control
          const transformedStickValue = cubicDeadzoneFunction(
            GAMEPAD_DEADZONE,
            payload.expoLevel
          )(value);
          // set all directions corresponding to axis to 0 if the transformed value is 0, which means
          // that we are within deadzone
          if (Math.abs(transformedStickValue) < kMilliTol) {
            for (const direction of directions) {
              state.gamepadMovementInputs[direction] = 0;
            }
          } else {
            // a gamepad axis can have multiple directions if it is a joystick and not a button
            // directions[0] is for the -'ve direction, directions[1] is for the +'ve direction
            const intendedDirection =
              directions.length > 1 && transformedStickValue > 0 ? directions[1] : directions[0];
            state.gamepadMovementInputs[intendedDirection] = Math.abs(transformedStickValue);
          }
        }
      }
      // clear desiredPose and clickToFly if the gamepad is being used
      pilotCaseReducers.clearDesiredPose(state);
      if (state.clickToFly.active > 0) {
        pilotCaseReducers.clearClickToFly(state);
      }
    },
    setTeleopActionSources(state, { payload }: PayloadAction<{ [key in TeleopAction]?: boolean }>) {
      for (const [action, gamepad] of Object.entries(payload)) {
        state.teleopActionSources[action as TeleopAction] = gamepad ? "gamepad" : "keyboard";
      }
    },
    setSubjectTrackingControlMode(state, { payload }: PayloadAction<"mouse" | "gamepad">) {
      state.teleopSubjectTrackingControlMode = payload;
    },
    handleButtonInput(state, { payload }: PayloadAction<ButtonActionPayload>) {
      state.buttonInputs[payload.key] = payload.value;
    },
    handlePointerLockState(state, { payload: nowLocked }: PayloadAction<boolean>) {
      state.mouseInput.locked = nowLocked;
      if (!nowLocked) {
        // clear pointer lock orientation when we exit
        state.mouseInput.orientation = {};
      } else {
        // clear any desired pose when entering pointer lock, otherwise we will snap back to this
        // heading when exiting pointer lock
        pilotCaseReducers.clearDesiredPose(state);
        state.mouseInput.orientation = {};
      }
    },
    handleMouseInput(state, { payload }: PayloadAction<Partial<MouseOrientation>>) {
      // set the mouse input only if it has changed to make sure the timstamp set is consistent with
      // the timestamp of the command
      const pitchChanged = !areNumbersApproximatelyEqual(
        state.mouseInput.orientation.pitch,
        payload.pitch
      );
      const headingChanged = !areNumbersApproximatelyEqual(
        state.mouseInput.orientation.heading,
        payload.heading
      );
      if (headingChanged || pitchChanged) {
        state.mouseInput.orientation = payload;
        state.mouseInput.lastMouseMove = performance.now();
      }
    },
    setClickToFly(
      state,
      action: PayloadAction<{ clickStart: Vector3; clickEnd: Vector3; scrollEnd: Vector3 }>
    ) {
      state.clickToFly.active = performance.now();
      state.clickToFly.clickStart = action.payload.clickStart.clone();
      state.clickToFly.clickEnd = action.payload.clickEnd.clone();
      state.clickToFly.scrollEnd = action.payload.scrollEnd.clone();

      // add a manual flight blocker so that keyboard inputs don't command a desired position
      if (!state.manualFlightBlockers.blockers.includes(MANUAL_FLIGHT_BLOCKERS.CLICK_TO_FLY)) {
        state.manualFlightBlockers.blockers.push(MANUAL_FLIGHT_BLOCKERS.CLICK_TO_FLY);
        state.manualFlightBlockers.blocked = true;
      }
    },
    clearClickToFly(state) {
      state.clickToFly.active = -1;
      // set the mouse input to the current camera orientation to prevent it snapping back to what
      // it was before click to fly once regular teleop resumes
      const navTGimbalCamImu = state.nav_T_gimbal_camera_imu
        ? convertTransPbToObject3(state.nav_T_gimbal_camera_imu.transform)
        : undefined;
      const imuTCam = state.imu_T_subject_cam
        ? convertTransPbToObject3(state.imu_T_subject_cam)
        : undefined;
      const gimbalCamRImu = state.gimbal_cam_R_imu;
      const navTCommand = calculateNavTCommand(navTGimbalCamImu, imuTCam, gimbalCamRImu);
      const orientation = calculateNavHeadingPitchFromGimbalAndVehicleOrientation(
        navTCommand?.quaternion,
        state.vehicleOrientation
      );
      if (orientation !== undefined) {
        state.mouseInput.orientation = orientation;
      }
      // remove the click to fly manual flight blocker
      if (state.manualFlightBlockers.blockers.includes(MANUAL_FLIGHT_BLOCKERS.CLICK_TO_FLY)) {
        state.manualFlightBlockers.blockers = state.manualFlightBlockers.blockers.filter(
          blocker => blocker !== MANUAL_FLIGHT_BLOCKERS.CLICK_TO_FLY
        );
        state.manualFlightBlockers.blocked = state.manualFlightBlockers.blockers.length > 0;
        state.manualFlightBlockers.blockerRemovalTimestamp = performance.now();
      }
    },
    toggleCrawlModeActive(state) {
      state.crawlModeActive = !state.crawlModeActive;
      // If crawl is enabled, make sure to disable boost. Resolves SW-67722.
      state.boostModeActive = false;
    },
    clearCrawlMode(state) {
      state.crawlModeActive = false;
    },
    toggleBoostModeActive(state) {
      state.boostModeActive = !state.boostModeActive;
      // If boost is enabled, make sure to disable crawl. Resolves SW-67722.
      state.crawlModeActive = false;
    },
    clearBoostMode(state) {
      state.boostModeActive = false;
    },
    handleSkillStatus(state, { payload }: PayloadAction<SkillStatusMinimal.AsObject>) {
      state.skillState = payload.skillKey;
    },
    handleBlockingPrompt(state, action: PayloadAction<BlockingPrompt.AsObject | undefined>) {
      if (!_.isEqual(state.blockingPrompt, action.payload)) {
        state.blockingPrompt = action.payload;
      }
    },
    handleActivePrompts(state, action: PayloadAction<BlockingPrompt.AsObject[]>) {
      if (!_.isEqual(state.activePromptsList, action.payload)) {
        state.activePromptsList = action.payload;
      }
    },
    setSessionId(state, action: PayloadAction<string>) {
      state.sessionId = action.payload;
    },
    setRunmode(state, action: PayloadAction<string>) {
      state.runmode = action.payload;
    },
    setCameraTrans(state, { payload }: PayloadAction<TransPb.AsObject>) {
      state.imu_T_subject_cam = payload;
    },
    setNavTGimbalImu(state, { payload }: PayloadAction<TransPb.AsObject>) {
      state.nav_T_gimbal_camera_imu = { transform: payload, timestamp: performance.now() };
    },
    setCameraInstrinsics(state, { payload }: PayloadAction<CameraIntrinsics.AsObject>) {
      state.cameraIntrinsics = payload;
    },
    disablePointerLockForAutonomy(state) {
      // If the vehicle enters a state of autonomous flight, unlock the pointer
      // and clear the desired pose so the vehicle does not snap back to these points after
      state.mouseInput.locked = false;
      pilotCaseReducers.clearDesiredPose(state);
      pilotCaseReducers.clearClickToFly(state);
      state.mouseInput.orientation = {};
    },
    handleMotionPlannerStatus(state, action: PayloadAction<MotionPlannerStatus.AsObject>) {
      state.movingAutonomously = action.payload.isMovingAutonomously;
      state.isAutonomyOff = action.payload.isAutonomyOff;
      state.policyCommandType = action.payload.policyCommandType;
    },
    handlePilotResponse(state, { payload }: PayloadAction<PilotResponse.AsObject>) {
      state.mostRecentPilot.updatedAt = Date.now();
      state.mostRecentPilot.clientId = nextPilotClientId(state, payload);
      state.commandeerRequested = nextCommandeerRequested(state);
    },
    handleFlightDeckServerInfo(state, { payload }: PayloadAction<ServerInfo.AsObject>) {
      state.mostRecentPilot.updatedAt = Date.now();
      state.mostRecentPilot.clientId = payload.pilotId;
      state.commandeerRequested = nextCommandeerRequested(state);
    },
    clearDirectionalKeyboardInputs(state) {
      for (const input of Object.keys(state.keyboardMovementInputs)) {
        state.keyboardMovementInputs[input as MovementDirection] = -1;
      }
      pilotCaseReducers.clearDesiredPose(state);
    },
    clearMouseInput(state) {
      state.mouseInput.orientation = {};
      state.mouseInput.lastMouseMove = -1;
      state.mouseInput.locked = false;
    },
    clearDirectionalGamepadInputs(state) {
      for (const input of Object.keys(state.gamepadMovementInputs)) {
        state.gamepadMovementInputs[input as MovementDirection] = 0;
      }
    },
    clearKeyboardTeleopActionInputs(state) {
      for (const action of Object.keys(state.teleopActionInputs)) {
        if (state.teleopActionSources[action as TeleopAction] === "keyboard") {
          state.teleopActionInputs[action as TeleopAction] = -1;
        }
      }
    },
    clearGamepadTeleopActionInputs(state) {
      for (const action of Object.keys(state.teleopActionInputs)) {
        if (state.teleopActionSources[action as TeleopAction] === "gamepad") {
          state.teleopActionInputs[action as TeleopAction] = -1;
        }
      }
    },
    addManualFlightBlocker(state, { payload }: PayloadAction<MANUAL_FLIGHT_BLOCKERS>) {
      // add the blocker if it's not already there
      if (!state.manualFlightBlockers.blockers.includes(payload)) {
        state.manualFlightBlockers.blockers.push(payload);
        state.manualFlightBlockers.blocked = true;
      }
    },
    removeManualFlightBlocker(state, { payload }: PayloadAction<MANUAL_FLIGHT_BLOCKERS>) {
      // remove specific blocker
      state.manualFlightBlockers.blockers = state.manualFlightBlockers.blockers.filter(
        blocker => blocker !== payload
      );
      state.manualFlightBlockers.blocked = state.manualFlightBlockers.blockers.length > 0;
      state.manualFlightBlockers.blockerRemovalTimestamp = performance.now();
    },
    clearManualFlightBlockers(state) {
      state.manualFlightBlockers.blocked = false;
      state.manualFlightBlockers.blockers = [];
      state.manualFlightBlockers.blockerRemovalTimestamp = performance.now();
    },
    clearTeleopActionSources(state) {
      for (const action of Object.keys(state.teleopActionSources)) {
        state.teleopActionSources[action as TeleopAction] = "keyboard";
      }
    },
    setVehiclePosition(state, { payload }: PayloadAction<Array<number>>) {
      state.vehiclePosition = new Vector3(payload[0], payload[1], payload[2]);
    },
    setVehicleVelocity(state, { payload }: PayloadAction<Array<number>>) {
      const translationalVelocity = new Vector3(payload[0], payload[1], payload[2]);
      state.vehicleVelocity = translationalVelocity;
    },
    setVehicleOrientation(state, { payload }: PayloadAction<Array<number>>) {
      const orientation = new Quaternion(payload[0], payload[1], payload[2], payload[3]);
      state.vehicleOrientation = orientation;
    },
    setDesiredPose(
      state,
      { payload }: PayloadAction<{ position?: Vector3; heading?: number; pitch?: number }>
    ) {
      // Check if the desired pose has changed and only set it if it has to make sure the timestamp
      // set is consistent with the timestamp of the command
      const positionChanged =
        payload.position &&
        !areVectorsApproximatelyEqual(payload.position, state.desiredPose.position);
      const headingChanged =
        payload.heading &&
        !areNumbersApproximatelyEqual(payload.heading, state.desiredPose.heading);
      const pitchChanged =
        payload.pitch && !areNumbersApproximatelyEqual(payload.pitch, state.desiredPose.pitch);
      if (positionChanged || headingChanged || pitchChanged) {
        state.desiredPose = {
          position: payload.position,
          heading: payload.heading,
          pitch: payload.pitch,
          timestamp: performance.now(),
        };

        // Set the control modes to "position" so that the values will be picked up
        state.positionControlMode = "position";
        state.headingControlMode = "position";
        state.pitchControlMode = "position";
      }
    },
    gimbalPitchDownMouseInput(state) {
      state.mouseInput.orientation = {
        pitch: degToRad(90),
        heading: state.mouseInput.orientation.heading,
      };
      state.mouseInput.lastMouseMove = performance.now();
    },
    gimbalPitchDownPosition(state) {
      state.desiredPose = {
        pitch: degToRad(90),
        heading: state.desiredPose.heading,
        position: state.desiredPose.position,
        timestamp: performance.now(),
      };
    },
    gimbalPitchResetMouseInput(state) {
      state.mouseInput.orientation = {
        pitch: degToRad(0),
        heading: state.mouseInput.orientation.heading,
      };
      state.mouseInput.lastMouseMove = performance.now();
    },
    gimbalPitchResetPosition(state) {
      state.desiredPose = {
        pitch: degToRad(0),
        heading: state.desiredPose.heading,
        position: state.desiredPose.position,
        timestamp: performance.now(),
      };
    },

    // Reset function - toggles between 0deg (forward) and 90deg (down), defaulting to 0deg
    resetGimbalPitch(state) {
      if (state.mouseInput.locked && state.mouseInput.orientation.pitch != null) {
        const currentPitch = state.mouseInput.orientation.pitch;

        // If currently at 0deg (forward), go to 90deg (down)
        // Otherwise, go to 0deg (forward) - this is the "default to forward" behavior
        if (Math.abs(currentPitch - degToRad(0)) < degToRad(5)) {
          pilotCaseReducers.gimbalPitchDownMouseInput(state);
        } else {
          pilotCaseReducers.gimbalPitchResetMouseInput(state);
        }
      } else {
        state.pitchControlMode = "position";
        const currentPitch = state.desiredPose.pitch ?? degToRad(90);

        // If currently at 0deg (forward), go to 90deg (down)
        // Otherwise, go to 0deg (forward) - this is the "default to forward" behavior
        if (Math.abs(currentPitch - degToRad(0)) < degToRad(5)) {
          pilotCaseReducers.gimbalPitchDownPosition(state);
        } else {
          pilotCaseReducers.gimbalPitchResetPosition(state);
        }
      }
    },

    // New function - goes to 90deg (down) first, then toggles to 0deg (forward) if already down
    toggleGimbalPitchDown(state) {
      if (state.mouseInput.locked && state.mouseInput.orientation.pitch != null) {
        const currentPitch = state.mouseInput.orientation.pitch;

        // Always go to 90deg (down) first - this is the "default to down" behavior
        // Only go to 0deg (forward) if already at 90deg (down)
        if (Math.abs(currentPitch - degToRad(90)) < degToRad(5)) {
          pilotCaseReducers.gimbalPitchResetMouseInput(state);
        } else {
          pilotCaseReducers.gimbalPitchDownMouseInput(state);
        }
      } else {
        state.pitchControlMode = "position";
        const currentPitch = state.desiredPose.pitch ?? degToRad(0); // Default to 0deg (forward) so it goes to 90deg first

        // Always go to 90deg (down) first - this is the "default to down" behavior
        // Only go to 0deg (forward) if already at 90deg (down)
        if (Math.abs(currentPitch - degToRad(90)) < degToRad(5)) {
          pilotCaseReducers.gimbalPitchResetPosition(state);
        } else {
          pilotCaseReducers.gimbalPitchDownPosition(state);
        }
      }
    },

    // New function - always goes to 90deg (down)
    gimbalPitchLookDown(state) {
      if (state.mouseInput.locked && state.mouseInput.orientation.pitch != null) {
        pilotCaseReducers.gimbalPitchDownMouseInput(state);
      } else {
        state.pitchControlMode = "position";
        pilotCaseReducers.gimbalPitchDownPosition(state);
      }
    },

    // New function - always goes to 0deg (forward)
    gimbalPitchLookForward(state) {
      if (state.mouseInput.locked && state.mouseInput.orientation.pitch != null) {
        pilotCaseReducers.gimbalPitchResetMouseInput(state);
      } else {
        state.pitchControlMode = "position";
        pilotCaseReducers.gimbalPitchResetPosition(state);
      }
    },
    clearDesiredPose(state) {
      state.desiredPose = {};
    },
    setVehicleTranslationSpeed(state, { payload }: PayloadAction<number>) {
      state.vehicleNominalTranslationSpeed = payload;
      state.vehicleSpeedChangeTime = performance.now();
    },
    changeDesiredVehicleTranslationSpeed(state, { payload }: PayloadAction<number>) {
      state.desiredVehicleNominalTranslationSpeed = payload;
      state.vehicleSpeedChangeTime = performance.now();
    },
    setDesiredZoomLevel(state, { payload }: PayloadAction<number>) {
      state.desiredZoomLevel = payload;
      state.zoomCorrectionFactor = getZoomCorrection(payload);
    },
    incrementVehicleTranslationSpeed(
      state,
      { payload }: PayloadAction<{ speed: number; showToast: boolean }>
    ) {
      const desiredNominalTranslationSpeed = state.vehicleNominalTranslationSpeed + payload.speed;
      const antMessageClassName = getTeleopAntMessageClassName();
      // display messages depending on speed increment commanded
      if (payload.speed < 0) {
        if (desiredNominalTranslationSpeed < state.vehicleSpeedSettings.min) {
          toast.warning("Already at minimum speed", {
            className: antMessageClassName,
            id: "speed-hotkey",
            duration: 3000,
          });
        } else if (payload.showToast) {
          toast.info("Speed setting decreased", {
            className: antMessageClassName,
            id: "speed-hotkey",
            duration: 3000,
          });
        }
      } else {
        if (desiredNominalTranslationSpeed > state.vehicleSpeedSettings.max) {
          toast.warning("Already at maximum speed", {
            className: antMessageClassName,
            id: "speed-hotkey",
            duration: 3000,
          });
        } else if (payload.showToast) {
          toast.info("Speed setting increased", {
            className: antMessageClassName,
            id: "speed-hotkey",
            duration: 3000,
          });
        }
      }
      state.desiredVehicleNominalTranslationSpeed = _.clamp(
        desiredNominalTranslationSpeed,
        state.vehicleSpeedSettings.min,
        state.vehicleSpeedSettings.max
      );
      state.vehicleNominalTranslationSpeed = _.clamp(
        desiredNominalTranslationSpeed,
        state.vehicleSpeedSettings.min,
        state.vehicleSpeedSettings.max
      );
      state.vehicleSpeedChangeTime = performance.now();
    },
    displayPointerLockInnerRing(state, { payload }: PayloadAction<boolean>) {
      state.displayPointerLockInnerRing = payload;
    },
    displayClickToFlyTrail(state, { payload }: PayloadAction<boolean>) {
      state.clickToFly.displayTrail = payload;
    },
    handleGoToWaypointStatus(state, { payload }: PayloadAction<GoToWaypointStatus>) {
      // set waypoint state
      const waypointState = payload.getState();
      const waypointSource = payload.getSource();
      state.goToWaypointStatus = { state: waypointState, source: waypointSource };
    },
    handleReturnToDockInfo(state, { payload }: PayloadAction<ReturnToDockInfo.AsObject>) {
      state.returnToDockInfo = payload;
    },
    handleSafeLandingInfo(state, { payload }: PayloadAction<SafeLandingInfo.AsObject>) {
      state.safeLandingInfo = payload;

      if (payload.targetId) {
        state.latestValidTargetLandingPointId = payload.targetId;
      }
    },
    handleGimbalFlashlightStatus(
      state,
      { payload }: PayloadAction<GimbalFlashlightStatus.AsObject>
    ) {
      state.gimbalFlashlightStatus = payload;
    },
    setThirdPersonView(state, { payload }: PayloadAction<boolean>) {
      state.thirdPersonViewActive = payload;
    },
    handleStreamCameraCalibration: (
      state,
      { payload }: PayloadAction<StreamCameraCalibration.AsObject>
    ) => {
      state.streamCameraCalibration = payload;

      const eoImuTCam = payload.eoCamera?.imuTCam;
      if (eoImuTCam) {
        state.eo_imu_T_cam = toTransPb(eoImuTCam, payload.utime);
      }

      const irImuTCam = payload.irCamera?.imuTCam;
      if (irImuTCam) {
        state.ir_imu_T_cam = toTransPb(irImuTCam, payload.utime);
      }

      const gimbalCamRImu = payload.gimbalCamRImu;
      if (gimbalCamRImu) {
        state.gimbal_cam_R_imu = new Quaternion().fromArray(gimbalCamRImu.xyzwList);
      }
    },
    setConfigReceived(state, { payload }: PayloadAction<boolean>) {
      state.configReceived = payload;
    },
    handleLandingPadTrackerState(
      state,
      { payload }: PayloadAction<LandingPadTrackerSimplifiedState.AsObject>
    ) {
      state.landingPadTracker = payload;
    },
    clearLandingPadTrackerState(state) {
      state.landingPadTracker = undefined;
    },
    handleUserCameraAutoRegionState(
      state,
      { payload }: PayloadAction<MinimalAutoRegionState.AsObject>
    ) {
      state.userCameraAutoRegionState = payload;
    },
    setDefaultOrbitInterceptMode(state, { payload }: PayloadAction<InterceptOrbitPoiMode_Enum>) {
      state.defaultOrbitInterceptMode = payload;
    },
    setDefaultOrbitDirection(
      state,
      { payload }: PayloadAction<PilotState["defaultOrbitDirection"]>
    ) {
      state.defaultOrbitDirection = payload;
    },
    setDefaultPathBetweenPointsMode(
      state,
      { payload }: PayloadAction<PilotState["defaultPathBetweenPointsMode"]>
    ) {
      state.defaultPathBetweenPointsMode = payload;
    },
    setDefaultLookAtPointActive(state, { payload }: PayloadAction<boolean>) {
      state.defaultLookAtPointActive = payload;
    },
    setRampHeightModeActive(state, { payload }: PayloadAction<boolean>) {
      state.rampHeightModeActive = payload;
    },
    handleFlightPhasePb(state, { payload }: PayloadAction<FlightPhasePb.AsObject>) {
      state.flightId = payload.flightId;
      state.flightPhaseId = payload.phaseId;
      state.blockingFaultsPb = payload.blockingFaultsList;
    },
    handleFlightPhaseLcm(state, { payload }: PayloadAction<flight_phase_t>) {
      state.flightId = payload.flight_id;
      state.flightPhaseId = payload.phase_id.value as unknown as ValueType<FlightPhaseEnum.EnumMap>;
    },
    setLookAtFinalPointActive(state, { payload }: PayloadAction<boolean>) {
      state.lookAtFinalPointActive = payload;
    },
    handleAttachmentPortStatus(state, { payload }: PayloadAction<PortStatusToConsumers.AsObject>) {
      state.attachmentPortStatus = payload;
    },
    handleSpotlightSystemStatus(state, { payload }: PayloadAction<SpotlightSystemStatus>) {
      state.spotlightSystemStatus = payload;
    },
    handleDeviceLinksSummary(state, { payload }: PayloadAction<DeviceLinksSummary>) {
      state.deviceLinksSummary = { ...state.deviceLinksSummary, [payload.getSerial()]: payload };
    },
    handleDeviceNodeHealthSummary(state, { payload }: PayloadAction<DeviceNodeHealth>) {
      state.deviceNodeHealth = { ...state.deviceNodeHealth, [payload.getSerial()]: payload };
    },
    resetSkillState(state) {
      state.skillState = undefined;
    },
    handleSpeakerMicStatus(state, { payload }: PayloadAction<SpeakerMicStatus>) {
      state.speakerMicStatus = payload;
    },
    handleSpeakerMicAudioFiles(state, { payload }: PayloadAction<SpeakerMicAudioFiles>) {
      state.speakerMicAudioFiles = payload;
    },
    // DroneSense mission integration
    handleDroneSenseRequestStatusUpdated(
      state,
      { payload }: PayloadAction<"UNINITIALIZED" | "REQUESTED" | "ERROR">
    ) {
      state.droneSense.requestStatus = payload;
    },
    handleDroneSenseMissionListUpdated(
      state,
      {
        payload: { missions, selectedMissionId, callSign },
      }: PayloadAction<{
        missions: DroneSenseMission[];
        selectedMissionId?: string;
        callSign: string;
      }>
    ) {
      state.droneSense = {
        requestStatus: "SUCCESS",
        callSign,
        selectedMissionId: selectedMissionId ?? missions[0]?.missionId,
        missions,
      };
    },
    handleDroneSenseMissionSelected(state, { payload }: PayloadAction<string>) {
      if (state.droneSense.requestStatus === "SUCCESS") {
        state.droneSense.selectedMissionId = payload;
      }
    },
    handleDroneSenseCallSignUpdated(state, { payload }: PayloadAction<string>) {
      if (state.droneSense.requestStatus === "SUCCESS") {
        state.droneSense.callSign = payload;
      }
    },
    setFarAwayFromLaunchPoint(state, action: PayloadAction<boolean>) {
      state.farAwayFromLaunchPoint = action.payload;
    },
    setFarAwayFromLaunchPointDismissed(state, action: PayloadAction<boolean>) {
      state.farAwayFromLaunchPointDismissed = action.payload;
    },
    setFlyToPointStandoffDistance(state, { payload }: PayloadAction<number>) {
      state.flyToPointStandoffDistance = payload;
    },
    handleParachuteStatus(state, { payload }: PayloadAction<ParachuteStatus>) {
      state.parachuteStatus = payload;
    },
    handleDropperStatus(state, { payload }: PayloadAction<DropperStatus>) {
      state.dropperStatus = payload;
    },
    setSubjectTrackingIndex(state, action: PayloadAction<number>) {
      state.subjectTrackingIndex = action.payload;
    },
    handleSubjectStatus(state, action: PayloadAction<MinimalSubjectStatus>) {
      state.subjectState = action.payload.getState();
      if (action.payload.hasFollowBlockedReason()) {
        state.subjectFollowBlockedReason = action.payload.getFollowBlockedReason();
      } else {
        state.subjectFollowBlockedReason = undefined;
      }
    },
    setAutonomyWidgetStatus(state, action: PayloadAction<AutonomyWidgetStatus>) {
      state.autonomyWidgetStatus = action.payload;
    },
    /**
     * Store scenario_sim status from PARAVERSE_SCENARIO_STATUS_PB.
     * Widget stays visible for ALL outcomes as long as data keeps arriving on the topic.
     */
    paraverseScenarioStatusReceived(state, action: PayloadAction<ScenarioStatus.AsObject>) {
      state.paraverseScenarioStatus = action.payload;
    },
    paraverseScenarioClear(state) {
      state.paraverseScenarioStatus = undefined;
    },
    setTurtleModeRecommendation(
      state,
      action: PayloadAction<ValueType<TurtleModeDirection.EnumMap>>
    ) {
      state.turtleModeRecommendation = action.payload;
    },
    setVehicleCrosshairPosition(
      state,
      action: PayloadAction<PilotState["vehicleCrosshairPosition"]>
    ) {
      state.vehicleCrosshairPosition = action.payload;
    },
    handleCellularModemStates(state, { payload }: PayloadAction<CellularModemStates>) {
      state.cellularModemStates = payload;

      // A22 attachment modem (has attachment serial)
      const a22Modem = payload.getStatesList().find(state => state.getAttachmentSerial());
      if (a22Modem) {
        const options = a22Modem.getSimSlotsList().map((slot, index) => {
          const simState = slot.getSimState();
          const matched = simState === SimState.Enum.KMATCHED;
          const label = matched
            ? (slot.getSimConfig()?.getCarrier() ?? `Slot ${index + 1}`)
            : getSimSlotInactiveReason(simState, index + 1);
          return {
            label,
            value: index,
            disabled: !matched,
          };
        });
        if (!_.isEqual(state.a22SimOptions, options)) {
          state.a22SimOptions = options;
        }
      }

      // Vehicle built-in modem with multiple SIM slots (R71)
      const vehicleModem = payload
        .getStatesList()
        .find(state => !state.getAttachmentSerial() && state.getSimSlotsList().length > 1);
      if (vehicleModem) {
        const options = vehicleModem.getSimSlotsList().map((slot, index) => {
          const simState = slot.getSimState();
          const matched = simState === SimState.Enum.KMATCHED;
          const label = matched
            ? (slot.getSimConfig()?.getCarrier() ?? `Slot ${index + 1}`)
            : getSimSlotInactiveReason(simState, index + 1);
          return {
            label,
            value: index,
            disabled: !matched,
          };
        });
        if (!_.isEqual(state.vehicleSimOptions, options)) {
          state.vehicleSimOptions = options;
        }
      }
    },
    handleUserCameraStatus(state, action: PayloadAction<CameraStatus>) {
      state.userCameraStatus = action.payload;
    },
    setOrgAutonomousFlightSpeed(state, action: PayloadAction<number>) {
      state.autonomousFlightSpeed.policySetting = action.payload;
    },
    setUserAutonomousFlightSpeed(state, action: PayloadAction<number>) {
      state.autonomousFlightSpeed.userSetting = action.payload;
    },
    setRemoteIdBroadcasting(state, action: PayloadAction<boolean>) {
      state.remoteIdBroadcasting = action.payload;
    },
  },
  extraReducers: builder =>
    builder
      .addCase(teleopActions.updateTeleopSessionId, state => {
        // Clear state when we get a new teleop sesion ID; we do not clear takeoffRequested to ensure
        // the result of autoPrep doesn't get clobbered on the initial app load
        return {
          ...initialState,
          takeoffRequested: state.takeoffRequested,
        };
      })
      .addCase(teleopActions.requestPilot, (state, action) => {
        // request commandeer/takeoff if pilot is requested with commandeer option
        if (action.payload.options?.commandeer === true) {
          state.commandeerRequested = true;
          state.takeoffRequested = true;
        }
      }),
});

export { pilotActions };

export const handleMotionPlannerUpdate =
  (payload: MotionPlannerStatus.AsObject): ThunkAction<void, AppState, unknown, Action<string>> =>
  (dispatch, getState) => {
    const { pilot, mission } = getState();
    if (!pilot.movingAutonomously && payload.isMovingAutonomously && !mission.freeLookActive) {
      dispatch(pilotActions.disablePointerLockForAutonomy());
    }
    dispatch(pilotActions.handleMotionPlannerStatus(payload));
  };

export default reducer;
